import type { ReactNode } from 'react';
import React, { Children, useEffect, useRef, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { NAVIGATION_OFFSET_SELECTOR } from 'Components/widgets/navigation/Navigation.constants';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { t } from 'Src/i18n';
import { Icon } from 'Components/frameworks/icon/Icon';
import { themes } from './SeeMore.themes';
export type SeeMoreProps = {
  children: ReactNode;
  className?: string;
  classNameButton?: string;
  color?: Colors;
  hasDynamicHeight?: boolean;
  hasNoScrollOnCollapse?: boolean;
  isCollapseEnabled?: boolean;
  offsetScrollSelector?: string;
  onClick?: (arg0: React.SyntheticEvent, arg1: boolean) => void;
  seeLessLabel?: string;
  seeMoreLabel?: string;
  seeMoreSize?: number;
  seeMoreSizeTolerance?: number;
  theme?: keyof typeof themes;
};
export const SeeMoreEngine = ({
  children,
  className,
  classNameButton,
  color = COLORS.WHITE,
  hasDynamicHeight = false,
  hasNoScrollOnCollapse = false,
  isCollapseEnabled = true,
  offsetScrollSelector = NAVIGATION_OFFSET_SELECTOR,
  onClick,
  seeLessLabel = t('common.loadLess'),
  seeMoreLabel = t('common.loadMore'),
  seeMoreSize = 120,
  seeMoreSizeTolerance = 30,
  theme = 'default',
}: SeeMoreProps) => {
  const [height, setHeight] = useState(0);
  const [isCollapsable, setIsCollapsable] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const ref = useRef(null);
  const seeMoreSizeTolerated = seeMoreSize + seeMoreSizeTolerance;
  useEffect(() => {
    if (children && ref?.current?.clientHeight && ref.current.clientHeight > seeMoreSizeTolerated) {
      setHeight(ref.current.clientHeight);
    }
  }, [children, ref]);
  useEffect(() => {
    if (height > seeMoreSizeTolerated) {
      setIsCollapsable(true);
    }
  }, [height]);

  if (!Children.count(children)) {
    return null;
  }

  if (!isCollapseEnabled) {
    return children;
  }

  const { thButton } = themes[theme];
  return (
    <div className={className}>
      <div
        className="relative overflow-hidden transition-heights duration-250 ease-in"
        ref={ref}
        {...(hasDynamicHeight &&
          isCollapsable &&
          !isExpanded && {
            style: {
              height: seeMoreSize,
            },
          })}
        {...(!hasDynamicHeight &&
          isCollapsable && {
            style: {
              height: isExpanded ? height : seeMoreSize,
            },
          })}
      >
        {children}
        {isCollapsable && (
          <div
            className={classnames('absolute bottom-0 w-full', `bg-gradient-to-t from-${color}`, {
              'h-0 opacity-0': isExpanded,
              'h-50 opacity-100': !isExpanded,
            })}
          />
        )}
      </div>
      {isCollapsable && (
        <div className={classNameButton}>
          <button
            className={thButton}
            data-testid="CTA-see-more"
            onClick={(e) => {
              setIsExpanded(!isExpanded);
              onClick && onClick(e, isExpanded);

              if (isExpanded && !hasNoScrollOnCollapse) {
                scrollToElement(ref.current, 300, `.${offsetScrollSelector}`);
              }
            }}
            type="button"
          >
            {isExpanded ? seeLessLabel : seeMoreLabel}
            <Icon className="mx-5" name={isExpanded ? ICONS.MINUS : ICONS.PLUS} width="0.7em" />
          </button>
        </div>
      )}
    </div>
  );
};
